import React, {useState, useEffect, useRef}		from 'react';
import moment									from 'moment';
import DatePicker								from 'react-datepicker';
import SvgColor									from 'react-svg-color';

import Load										from '../Load/Load';
import Badge									from '../Badge';

import {useAppSelector}							from '../../../app/hooks';
import {InputProps}								from './interface';

import * as ft_divPosition						from '../../functions/divPosition';
import ft_cssVar								from '../../functions/getCssVar';

import img_email								from '../../images/icon/email.svg';
import img_alertCircle							from '../../images/icon/alert-circle.svg';
import img_upload								from '../../images/icon/upload.svg';
import img_eyeOpen								from '../../images/icon/eye-open.svg';
import img_eyeClose								from '../../images/icon/eye-close.svg';
import img_edit									from '../../images/icon/edit.svg';

import LANGUAGE									from '../../language';

import 'react-datepicker/dist/react-datepicker.css';
import './Input.css';


const defaultProps: InputProps = {
	fullWidth:    true,
	type:         'text',
	style:        {}
};


function Input(props: InputProps) {
	props = {...defaultProps, ...props};

	const lang = useAppSelector(state => state.settings.lang);

	const [change, set_change] = useState(false);
	const [isFocus, set_isFocus] = useState(false);
	const [hover, set_hover] = useState(false);
	const [open, set_open] = useState(false);
	const [drag, set_drag] = useState(false);
	const [showPassword, set_showPassword] = useState(false);
	const [absolutePosition, set_absolutePosition] = useState({top: 0, left: 0, minWidth: 0, maxWidth: 0});
	const [textViewText, set_textViewText] = useState('');
	const [textViewWidth, set_textViewWidth] = useState(0);
	const textViewEl = useRef<HTMLSpanElement>(null);
	const selectEl = useRef<HTMLDivElement>(null);
	const inputEl = useRef<HTMLInputElement>(null);

	const TEXTE = LANGUAGE(lang);

	useEffect(() => {
		if (textViewEl.current)
			set_textViewWidth(textViewEl.current.offsetWidth);
	}, [textViewText]);

	useEffect(() => {
		function handleScroll() {
			if (open && selectEl.current) {
				const position = ft_divPosition.getWindowPosition(selectEl.current);

				let maxWidth = (selectEl.current.offsetWidth - 2) * 4;

				if (maxWidth + position.left > window.innerWidth - 10)
					maxWidth = window.innerWidth - 10 - position.left;

				set_absolutePosition({
					maxWidth,
					left:     position.left,
					minWidth: selectEl.current.offsetWidth - 2,
					top:      (position.top + selectEl?.current?.offsetHeight) + 3
				});
			}
		}

		if (!open)
			set_absolutePosition({top: 0, left: 0, minWidth: 0, maxWidth: 0});

		handleScroll();

		const parentElements: HTMLElement[] = [];
		let 	currentElement: HTMLElement | null = selectEl.current;

		while (currentElement) {
			const parent = currentElement.parentElement;
			if (parent) {
				parentElements.push(parent);
				currentElement = parent;
			} else
				currentElement = null;
		}

		parentElements.forEach(element => element.addEventListener('scroll', handleScroll));

		return () => parentElements.forEach(element => element.removeEventListener('scroll', handleScroll));
	}, [open]);

	useEffect(() => set_change(false), [props.title]);

	function onChange(val: React.ChangeEvent<HTMLInputElement> | string, setOpen = true) {
		if (!change)
			set_change(true);

		if (props.searchAbsolute && setOpen)
			set_open(true);

		if (!props.onChange) {
			if (typeof val !== 'string' && props.type === 'file')
				return props.onChangeFile ? props.onChangeFile(val?.target?.files) : null;
			return;
		}

		if (typeof val === 'string') {
			set_textViewText(val);
			return props.onChange(val);
		}
		set_textViewText(val.target.value);
		return props.onChange(val.target.value);
	}

	function onChangeTag(value?: string, removeIndex?: number) {
		if (!props.tags || !props.setTags || (removeIndex === undefined && !value))
			return;

		const copy = [...props.tags];

		if (removeIndex !== undefined && removeIndex >= 0)
			copy.splice(removeIndex, 1);
		else if (value && !props.tags.find(find => find === value))
			copy.push(value);

		props.setTags(copy);

		if (props.onChange && removeIndex === undefined)
			props.onChange('');
	}

	function onFocus() {
		set_isFocus(true);

		if (props.onFocus)
			props.onFocus();
	}

	function renderTags(all = false) {
		const render: React.ReactElement[] = [];

		if (all) {
			props.allTags?.forEach((tag) => {
				const findIndex = props.tags?.findIndex(find => find === tag.name);

				render.push(
					<Badge
						checkbox
						childrenAfter
						size={'sm'}
						color={'tags'}
						text={tag.name}
						checked={findIndex !== -1}
						onClick={(findIndex === -1 ?
							() => onChangeTag(tag.name) : () => onChangeTag(undefined, findIndex))
						}
					>
						<Badge text={tag.count} color={'gray'} size={'sm'} />
					</Badge>
				);
			});
		} else {
			props.tags?.forEach((tag, index) => {
				render.push(
					<Badge
						text={tag}
						size={'sm'}
						color={'tags'}
						onClose={() => onChangeTag(undefined, index)}
					/>
				);
			});
		}

		return (render);
	}

	function renderFileInput() {
		const onDrag = (e: React.DragEvent<HTMLLabelElement>, isDrag = true) => {
			e.preventDefault();
			set_drag(isDrag);
		};
		const onDrop = (val: React.DragEvent<HTMLLabelElement>) => {
			val.preventDefault();
			set_drag(false);

			if (props.onChangeFile)
				props.onChangeFile(val?.dataTransfer.files);
		};

		return (
			<label
				onDrop={onDrop}
				data-drag={drag}
				onDragOver={onDrag}
				onDragEnter={onDrag}
				className={!props.children ? 'inputFileContainer' : ''}
				onDragLeave={e => onDrag(e, false)}
			>
				{props.children ?
					props.children
					:
					<div>
						{!props.noIconFileInput &&
							<div className={'inputFileContainerIcon'}>
								<SvgColor
									svg={img_upload}
									colors={drag ? [ft_cssVar('primary-500')] : ['#475467']}
								/>
							</div>
						}
						<span>
							<span className={'primary-700 weight-600'}>{TEXTE.component.input.clickToUpload}</span>
							{TEXTE.component.input.orDrag}
						</span>
						<span>{props.fileInputText}</span>
					</div>
				}
				<input
					type={'file'}
					onChange={onChange}
					accept={props.accept}
					style={props.children ? {visibility: 'hidden'} : {}}
					onClick={event => (event.target as HTMLInputElement).value = ''}
				/>
			</label>
		);
	}

	function renderAbsoluteSearch() {
		if (props.searchIsLoad)
			return <Load center size={18} />;

		const render: React.ReactElement[] = [];

		props.searchData?.forEach((e, i) => {
			const onClick = () => {
				set_open(false);

				if (props.onClickSearch)
					return props.onClickSearch(e.value as string);
				return onChange(e.value as string, false);
			};

			render.push(
				<div key={`select-${i}`} onClick={onClick}>
					<div className={'flex'}>{e.render}</div>
				</div>
			);
		});

		return render;
	}

	let	error = props.error === 'required' ? true : props.error;
	let value = props.value;

	if (props.notFocusValue && !isFocus)
		value = props.notFocusValue;
	if (!props.value && props.textView && !isFocus)
		value = props.placeholder;
	if (props.readOnly && props.readOnlySp) {
		props.type = 'text';
		if (!value)
			value = '-';
	}

	if (props.required && !props.value && change)
		error = true;
	else if (props.required && props.value && props.error === 'required')
		error = false;

	return (
		<div
			ref={selectEl}
			onMouseEnter={() => set_hover(true)}
			onMouseLeave={() => set_hover(false)}
			onBlur={() => {
				if (!hover)
					set_open(false);
				if (props.onBlur)
					props.onBlur();
			}}
			onClick={() => props.tags && inputEl.current?.focus()}
			className={`inputMainContainer ${props.fullWidth && 'fullWidth'}`}
		>
			{props.title &&
				<span className={'inputTitle'} style={props.styleTitle}>
					{props.title}{props.required && '*'}
				</span>
			}

			{props.type === 'file' ?
				renderFileInput()
				:
				<div
					className={`inputContainer ${error && 'error'} ${props.readOnly && 'readOnly'}  ${props.readOnlySp && 'sp'} ${props.textView && 'textView'} ${props.tags && 'inputTags'}`}
					style={props.style}
				>
					{props.textView &&
						<span className="hide text-lg weight-600" ref={textViewEl}>{textViewText || props.placeholder}</span>
					}

					{(props.before || props.type === 'email') &&
						<div className={'inputBefore'}>
							{props.type === 'email' && <img src={img_email} alt={'email'} />}
							{props.before}
						</div>
					}

					{props.tags && renderTags()}

					{props.type === 'date' ?
						<DatePicker
							dateFormat={'dd/MM/yyyy'}
							readOnly={props.readOnly}
							placeholderText={props.placeholder}
							className={'text-sm weight-400 gray-900'}
							onChange={val => onChange(moment(val).format('DD/MM/YYYY'))}
							selected={moment(props.value, 'DD/MM/YYYY').isValid() ? moment(props.value, 'DD/MM/YYYY').toDate() : undefined}
						/>
						:
						<input
							value={value}
							ref={inputEl}
							min={props.min}
							max={props.max}
							step={props.step}
							onFocus={onFocus}
							onChange={onChange}
							readOnly={props.readOnly}
							placeholder={props.placeholder}
							autoComplete={props.autoComplete}
							onBlur={() => !props.tags && set_isFocus(false)}
							style={props.textView ? {width: textViewWidth} : {}}
							className={`text-sm weight-400 gray-900 ${props.textView && 'text-lg weight-600'}`}
							onKeyDown={event => (event.key === 'Enter' ? onChangeTag(props.value as string) : null)}
							type={((props.notFocusValue && !isFocus) || showPassword) ? 'text' : props.type}
						/>
					}

					<div className={'inputIconContainer'}>
						{props.textView &&
							<div className={'inputAfter'} onClick={() => inputEl.current?.focus()}>
								<img src={img_edit} alt={'edit'} />
							</div>
						}

						{props.type === 'password' &&
							<div className={'inputAfter'} onClick={() => set_showPassword(!showPassword)}>
								<img src={showPassword ? img_eyeClose : img_eyeOpen} alt={''} />
							</div>
						}
						{error &&
							<div className={'inputAfter'}>
								<img src={img_alertCircle} alt={'alert'} />
							</div>
						}
					</div>
				</div>
			}

			{(props.searchAbsolute && open && absolutePosition.top !== 0) &&
				<div
					className={'selectAbsolute'}
					onClick={e => e.stopPropagation()}
					style={absolutePosition}
				>
					<div className={'selectAbsoluteContent'}>
						{renderAbsoluteSearch()}
					</div>
				</div>
			}

			{(error && typeof(error) !== 'boolean') &&
				<span className={'inputText error'}>
					{props.error}
				</span>
			}

			{(props.children && props.type !== 'file') &&
				<div className={'inputText'}>
					{props.children}
				</div>
			}

			{(props.tags && isFocus) &&
				<div className={'inputTagsContainer'} >
					{renderTags(true)}
				</div>
			}
		</div>
	);
}

export default Input;
